<template>
  <div class="main-layout">
    <!-- 移動端遮罩層 -->
    <div
      v-if="!sidebarCollapsed"
      class="sidebar-overlay"
      @click="toggleSidebar"
    ></div>
    
    <!-- 側邊欄 -->
    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="logo">
          <i class="ri-store-line"></i>
          <span v-if="!sidebarCollapsed" class="logo-text">LOCKLINE+</span>
        </div>
      </div>

      <!-- 動態選單 -->
      <nav class="sidebar-nav">
        <div v-if="menuStore.loading" class="menu-loading">
          <i class="ri-loader-4-line"></i>
          <span>載入選單中...</span>
        </div>

        <div v-else-if="menuStore.error" class="menu-error">
          <i class="ri-error-warning-line"></i>
          <span>{{ menuStore.error }}</span>
        </div>

        <ul v-else class="menu-list">
          <!-- Dashboard 選單項 -->
          <li class="menu-item">
            <router-link
              :to="dashboardRoute"
              class="menu-link"
              active-class="active"
            >
              <i class="ri-dashboard-line"></i>
              <span v-if="!sidebarCollapsed">Dashboard</span>
            </router-link>
          </li>

          <!-- 動態模組選單 -->
          <li
            v-for="module in menuStore.formattedMenu"
            :key="module.id"
            :ref="`module-${module.code}`"
            class="menu-item menu-group"
            @mouseenter="handleModuleHover(module.code, $event)"
            @mouseleave="handleModuleLeave(module.code)"
          >
            <div class="menu-group-header" @click="toggleModule(module.code)">
              <i :class="module.icon"></i>
              <span v-if="!sidebarCollapsed">{{ module.name }}</span>
              <i
                v-if="!sidebarCollapsed"
                :class="expandedModules[module.code] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                class="menu-arrow"
              ></i>
            </div>
            
            <!-- 普通模式的子選單 -->
            <ul
              v-if="expandedModules[module.code] && !sidebarCollapsed"
              class="menu-submenu"
            >
              <li
                v-for="program in module.children"
                :key="program.id"
                class="menu-subitem"
              >
                <router-link
                  :to="program.path"
                  class="menu-sublink"
                  active-class="active"
                >
                  <i :class="program.icon"></i>
                  <span>{{ program.name }}</span>
                </router-link>
              </li>
            </ul>
            
            <!-- 縮小模式的懸浮子選單 -->
            <div
              v-if="hoveredModules[module.code] && sidebarCollapsed"
              class="menu-submenu-floating"
              :style="getFloatingMenuStyle(module.code)"
            >
              <div class="submenu-header">{{ module.name }}</div>
              <ul class="submenu-list">
                <li
                  v-for="program in module.children"
                  :key="program.id"
                  class="menu-subitem"
                >
                  <router-link
                    :to="program.path"
                    class="menu-sublink"
                    active-class="active"
                  >
                    <i :class="program.icon"></i>
                    <span>{{ program.name }}</span>
                  </router-link>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>

      <!-- 使用者資訊 -->
      <div class="sidebar-footer">
        <div class="user-info">
          <div class="user-avatar">
            <i class="ri-user-line"></i>
          </div>
          <div v-if="!sidebarCollapsed" class="user-details">
            <div class="user-name">{{ (currentUser && currentUser.name) || (currentUser && currentUser.username) || '使用者' }}</div>
            <div class="user-role">{{ (currentUser && currentUser.role) || 'N/A' }}</div>
            <div class="user-account-type">{{ accountTypeText }}</div>
            <div v-if="accountType === 'client'" class="user-plan">
              <i class="ri-price-tag-3-line"></i>
              <span>{{ currentUser?.planName || clientPlanName || '未指定方案' }}</span>
            </div>
          </div>
        </div>
        <button class="logout-btn" @click="handleLogout" :title="sidebarCollapsed ? '登出' : ''">
          <i class="ri-logout-box-line"></i>
          <span v-if="!sidebarCollapsed">登出</span>
        </button>
      </div>
    </aside>

    <!-- 主內容區 -->
    <div class="main-content">
      <!-- 頂部導航欄 -->
      <header class="topbar" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <div class="topbar-left">
          <button class="mobile-menu-toggle" @click="toggleSidebar">
            <i :class="sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'"></i>
          </button>
          <span v-if="accountType === 'client'" class="topbar-plan-badge">
            <i class="ri-price-tag-3-line"></i>
            {{ currentUser?.planName || clientPlanName || '未指定方案' }}
          </span>
        </div>
        <div class="topbar-right">
          <button 
            @click="toggleHelpWindow"
            class="help-link-btn"
            title="操作說明"
          >
            <i class="ri-question-line"></i>
            <span class="help-text">操作說明</span>
          </button>
          <div class="user-menu-wrapper" @click.stop="toggleUserMenu">
            <button class="user-avatar-btn">
              <i class="ri-user-line"></i>
            </button>
            <div v-if="showUserMenu" class="user-menu-dropdown" @click.stop>
              <div class="user-menu-header">
                <div class="user-menu-avatar">
                  <i class="ri-user-line"></i>
                </div>
                <div class="user-menu-info">
                  <div class="user-menu-name">{{ (currentUser && currentUser.name) || (currentUser && currentUser.username) || '使用者' }}</div>
                  <div class="user-menu-role">{{ (currentUser && currentUser.role) || 'N/A' }}</div>
                  <div class="user-menu-account-type">{{ accountTypeText }}</div>
                  <div v-if="accountType === 'client'" class="user-menu-plan">
                    <i class="ri-price-tag-3-line"></i>
                    <span>{{ currentUser?.planName || clientPlanName || '未指定方案' }}</span>
                  </div>
                </div>
              </div>
              <div class="user-menu-divider"></div>
              <div class="user-menu-items">
                <button
                  v-if="accountType === 'client'"
                  class="user-menu-item"
                  @click="goToSubscription"
                >
                  <i class="ri-file-list-3-line"></i>
                  <span>訂閱與交易記錄</span>
                </button>
                <div v-if="isClientOwnerUser" class="user-menu-item-row">
                  <button
                    class="user-menu-item user-menu-item--grow"
                    @click="goToSystemSettings"
                  >
                    <i class="ri-settings-3-line"></i>
                    <span>系統參數</span>
                  </button>
                  <a
                    href="/client/settings"
                    target="_blank"
                    class="user-menu-item-newwin"
                    data-tip="在新視窗開啟"
                    @click.stop="showUserMenu = false"
                  >
                    <i class="ri-external-link-line"></i>
                  </a>
                </div>
                <button class="user-menu-item" @click="handleChangePassword">
                  <i class="ri-lock-password-line"></i>
                  <span>變更密碼</span>
                </button>
                <button class="user-menu-item logout-item" @click="handleLogout">
                  <i class="ri-logout-box-line"></i>
                  <span>登出</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- 訂閱過期提示 -->
      <div v-if="menuStore.subscriptionError" class="subscription-alert" :class="getSubscriptionAlertClass()">
        <div class="subscription-alert-content">
          <i :class="getSubscriptionAlertIcon()"></i>
          <span class="subscription-alert-message">{{ menuStore.subscriptionError.message }}</span>
          <button v-if="menuStore.subscriptionError.action === 'renew'" class="subscription-alert-action" @click="handleRenewSubscription">
            立即續費
          </button>
        </div>
      </div>

      <!-- 頁面內容 -->
      <main class="page-content" :class="{ 'with-subscription-alert': menuStore.subscriptionError }">
        <router-view :key="route.fullPath" />
      </main>
    </div>

    <!-- 浮動式操作說明視窗 -->
    <div
      v-show="showHelpWindow"
      class="help-window"
      :style="helpWindowStyle"
      @mousedown="startDrag"
    >
      <div class="help-window-header">
        <div class="help-window-title">
          <i class="ri-question-line"></i>
          <span>操作說明</span>
        </div>
        <div class="help-window-actions">
          <button @click="minimizeHelpWindow" class="help-window-btn" title="最小化">
            <i class="ri-subtract-line"></i>
          </button>
          <button @click="toggleHelpWindowSize" class="help-window-btn" title="切換大小">
            <i :class="isHelpWindowMaximized ? 'ri-picture-in-picture-exit-line' : 'ri-picture-in-picture-line'"></i>
          </button>
          <button @click="closeHelpWindow" class="help-window-btn help-window-close" title="關閉">
            <i class="ri-close-line"></i>
          </button>
        </div>
      </div>
      <div class="help-window-content">
        <iframe
          src="https://app.lockline.io/chat/ca93e528-744e-4ea6-b59d-1dc7eab93f85"
          frameborder="0"
          allowfullscreen
        ></iframe>
      </div>
      <div class="help-window-resize-handle" @mousedown.stop="startResize"></div>
    </div>

    <!-- 最小化狀態的浮動按鈕 -->
    <button
      v-if="isHelpWindowMinimized"
      @click="restoreHelpWindow"
      class="help-window-minimized"
      title="顯示操作說明"
    >
      <i class="ri-question-line"></i>
      <span>操作說明</span>
    </button>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMenuStore } from '@/stores/menu.js';
import authService from '@/helpers/authservice/authService.js';
import { isClientOwner as checkClientOwner } from '@/utils/clientAccount.js';
import { showConfirm, showSuccess, showError } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'MainLayout',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const menuStore = useMenuStore();
    const sidebarCollapsed = ref(false);
    const expandedModules = ref({});
    const hoveredModules = ref({});
    const floatingMenuPositions = ref({});
    const showUserMenu = ref(false);

    // 浮動視窗狀態
    const showHelpWindow = ref(false);
    const isHelpWindowMinimized = ref(false);
    const isHelpWindowMaximized = ref(false);
    const helpWindowPosition = ref({ x: 100, y: 100 });
    const helpWindowSize = ref({ width: 800, height: 600 });
    const isDragging = ref(false);
    const isResizing = ref(false);
    const dragStart = ref({ x: 0, y: 0 });
    const resizeStart = ref({ x: 0, y: 0, width: 0, height: 0 });
    const isHelpWindowInitialized = ref(false); // 記錄是否已初始化

    const currentUser = computed(() => authService.getCurrentUser());
    const accountType = computed(() => authService.getAccountType());
    const isClientOwnerUser = computed(() => checkClientOwner(currentUser.value));
    const clientPlanName = ref(null);
    
    // 是否為客戶帳號
    const isClientAccount = computed(() => {
      const type = accountType.value;
      return type === 'client';
    });
    
    // 獲取客戶方案資訊
    const fetchClientPlan = async () => {
      if (!isClientAccount.value || !currentUser.value?.clientId) {
        return;
      }
      
      // 如果用戶資訊中已經有方案名稱，直接使用
      if (currentUser.value?.planName) {
        clientPlanName.value = currentUser.value.planName;
        return;
      }
      
      // 如果沒有，嘗試從 API 獲取
      try {
        const { clientService } = await import('@/api/modules/clients.js');
        const response = await clientService.getClient(currentUser.value.clientId);
        if (response && response.success && response.data) {
          // API 返回的結構是 { clientData: { planName, planId, planCode, ... } }
          const clientData = response.data.clientData || response.data;
          const planName = clientData.planName;
          if (planName) {
            clientPlanName.value = planName;
            // 更新 localStorage 中的用戶資訊
            const updatedUser = {
              ...currentUser.value,
              planName: planName,
              planId: clientData.planId,
              planCode: clientData.planCode
            };
            localStorage.setItem('auth_user', JSON.stringify(updatedUser));
          }
        }
      } catch (error) {
        console.error('[MainLayout] 獲取客戶方案資訊失敗:', error);
      }
    };
    
    // 帳號類型文字
    const accountTypeText = computed(() => {
      const type = accountType.value;
      const typeMap = {
        'system': '系統帳號',
        'platform': '平台帳號',
        'client': '客戶帳號',
      };
      return typeMap[type] || '未知帳號類型';
    });

    // 根據帳號類型決定 Dashboard 路由
    const dashboardRoute = computed(() => {
      const type = accountType.value;
      if (type === 'system') {
        return '/system/dashboard';
      } else if (type === 'platform') {
        return '/platform/dashboard';
      } else if (type === 'client') {
        return '/client/dashboard';
      }
      return '/system/dashboard';
    });

    // 頁面標題：優先從資料庫讀取程序名稱，找不到則使用路由 meta.title
    const pageTitle = computed(() => {
      // 如果是 Dashboard，直接返回
      if (route.path.endsWith('/dashboard')) {
        return 'Dashboard';
      }

      // 從選單資料中查找對應的程序名稱
      const currentPath = route.path;
      const formattedMenu = menuStore.formattedMenu;
      
      // 如果選單資料還沒載入，先使用 meta.title
      if (!formattedMenu || formattedMenu.length === 0) {
        return (route.meta && route.meta.title) || 'Dashboard';
      }
      
      // 先嘗試完全匹配
      for (const module of formattedMenu) {
        for (const program of module.children || []) {
          if (program.path === currentPath) {
            return program.name;
          }
        }
      }

      // 如果完全匹配失敗，嘗試路徑前綴匹配（處理子路由，如 /create, /edit, /:id/edit）
      // 找到最長匹配的路徑
      let bestMatch = null;
      let bestMatchLength = 0;
      
      for (const module of formattedMenu) {
        for (const program of module.children || []) {
          if (program.path && currentPath.startsWith(program.path)) {
            // 確保是完整路徑段匹配（避免 /system/clients 匹配到 /system/client）
            // 例如：/system/clients 應該匹配 /system/clients/accounts，但不應該匹配 /system/client-xxx
            const pathEnd = currentPath[program.path.length];
            if (!pathEnd || pathEnd === '/' || pathEnd === '?') {
              if (program.path.length > bestMatchLength) {
                bestMatch = program.name;
                bestMatchLength = program.path.length;
              }
            }
          }
        }
      }

      if (bestMatch) {
        return bestMatch;
      }

      // 如果找不到，回退到路由 meta.title
      return (route.meta && route.meta.title) || 'Dashboard';
    });

    // 切換側邊欄
    const toggleSidebar = () => {
      sidebarCollapsed.value = !sidebarCollapsed.value;
    };

    // 浮動視窗相關函數
    const helpWindowStyle = computed(() => {
      if (isHelpWindowMaximized.value) {
        return {
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          transform: 'none'
        };
      }
      return {
        top: `${helpWindowPosition.value.y}px`,
        left: `${helpWindowPosition.value.x}px`,
        width: `${helpWindowSize.value.width}px`,
        height: `${helpWindowSize.value.height}px`
      };
    });

    const toggleHelpWindow = () => {
      showHelpWindow.value = !showHelpWindow.value;
      isHelpWindowMinimized.value = false;
      
      // 只在首次開啟時初始化視窗位置（螢幕中央）
      if (showHelpWindow.value && !isHelpWindowInitialized.value) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        helpWindowPosition.value = {
          x: (windowWidth - helpWindowSize.value.width) / 2,
          y: (windowHeight - helpWindowSize.value.height) / 2
        };
        isHelpWindowInitialized.value = true;
      }
    };

    const closeHelpWindow = () => {
      showHelpWindow.value = false;
      isHelpWindowMinimized.value = false;
      isHelpWindowMaximized.value = false;
    };

    const minimizeHelpWindow = () => {
      isHelpWindowMinimized.value = true;
      showHelpWindow.value = false;
    };

    const restoreHelpWindow = () => {
      isHelpWindowMinimized.value = false;
      showHelpWindow.value = true;
    };

    const toggleHelpWindowSize = () => {
      isHelpWindowMaximized.value = !isHelpWindowMaximized.value;
    };

    const startDrag = (e) => {
      if (isHelpWindowMaximized.value) return;
      if (e.target.closest('.help-window-actions') || e.target.closest('.help-window-resize-handle')) {
        return;
      }
      
      isDragging.value = true;
      dragStart.value = {
        x: e.clientX - helpWindowPosition.value.x,
        y: e.clientY - helpWindowPosition.value.y
      };
      
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', stopDrag);
    };

    const onDrag = (e) => {
      if (!isDragging.value) return;
      
      helpWindowPosition.value = {
        x: e.clientX - dragStart.value.x,
        y: e.clientY - dragStart.value.y
      };
    };

    const stopDrag = () => {
      isDragging.value = false;
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    const startResize = (e) => {
      if (isHelpWindowMaximized.value) return;
      
      isResizing.value = true;
      resizeStart.value = {
        x: e.clientX,
        y: e.clientY,
        width: helpWindowSize.value.width,
        height: helpWindowSize.value.height
      };
      
      document.addEventListener('mousemove', onResize);
      document.addEventListener('mouseup', stopResize);
    };

    const onResize = (e) => {
      if (!isResizing.value) return;
      
      const deltaX = e.clientX - resizeStart.value.x;
      const deltaY = e.clientY - resizeStart.value.y;
      
      helpWindowSize.value = {
        width: Math.max(400, resizeStart.value.width + deltaX),
        height: Math.max(300, resizeStart.value.height + deltaY)
      };
    };

    const stopResize = () => {
      isResizing.value = false;
      document.removeEventListener('mousemove', onResize);
      document.removeEventListener('mouseup', stopResize);
    };

    // 切換模組展開/收合
    const toggleModule = (moduleCode) => {
      if (!sidebarCollapsed.value) {
        expandedModules.value[moduleCode] = !expandedModules.value[moduleCode];
      }
    };

    // 處理模組 hover（縮小模式）
    const handleModuleHover = (moduleCode, event) => {
      if (sidebarCollapsed.value) {
        const target = event.currentTarget;
        const rect = target.getBoundingClientRect();
        
        floatingMenuPositions.value[moduleCode] = {
          top: rect.top,
          left: rect.right
        };
        
        hoveredModules.value[moduleCode] = true;
      }
    };

    // 處理模組 leave（縮小模式）
    const handleModuleLeave = (moduleCode) => {
      if (sidebarCollapsed.value) {
        hoveredModules.value[moduleCode] = false;
      }
    };

    // 取得浮動選單樣式
    const getFloatingMenuStyle = (moduleCode) => {
      const pos = floatingMenuPositions.value[moduleCode];
      if (!pos) return {};
      
      return {
        top: `${pos.top}px`,
        left: `${pos.left}px`
      };
    };

    // 切換使用者選單
    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value;
    };

    // 關閉使用者選單
    const closeUserMenu = () => {
      showUserMenu.value = false;
    };

    // 點擊外部關閉選單
    const handleClickOutside = (event) => {
      if (showUserMenu.value && !event.target.closest('.user-menu-wrapper')) {
        closeUserMenu();
      }
    };

    // 密碼強度驗證函數
    const validatePasswordStrength = (password) => {
      const requirements = {
        minLength: password.length >= 8,
        hasUpperCase: /[A-Z]/.test(password),
        hasLowerCase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_+\-=\[\]\\;',./]/.test(password)
      };
      
      const passedCount = Object.values(requirements).filter(v => v === true).length;
      const totalCount = Object.keys(requirements).length;
      
      let strength = 'weak';
      let strengthText = '弱';
      let strengthColor = '#dc3545';
      
      if (passedCount === totalCount) {
        strength = 'strong';
        strengthText = '強';
        strengthColor = '#28a745';
      } else if (passedCount >= 4) {
        strength = 'medium';
        strengthText = '中';
        strengthColor = '#ffc107';
      }
      
      const failedRequirements = [];
      if (!requirements.minLength) failedRequirements.push('至少 8 個字元');
      if (!requirements.hasUpperCase) failedRequirements.push('至少包含一個大寫字母');
      if (!requirements.hasLowerCase) failedRequirements.push('至少包含一個小寫字母');
      if (!requirements.hasNumber) failedRequirements.push('至少包含一個數字');
      if (!requirements.hasSpecialChar) failedRequirements.push('至少包含一個特殊字元');
      
      return {
        valid: requirements.minLength, // 只強制至少 8 碼
        strength,
        strengthText,
        strengthColor,
        passedCount,
        totalCount,
        requirements,
        failedRequirements
      };
    };

    // 變更密碼
    const handleChangePassword = async () => {
      closeUserMenu();
      
      // 創建密碼強度指示器的 HTML（更緊湊的設計）
      const passwordStrengthHTML = `
        <div id="password-strength-indicator" style="margin-top: 6px; font-size: 11px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
            <span style="color: #666; font-size: 11px;">強度：</span>
            <span id="strength-text" style="font-weight: 600; font-size: 11px;">-</span>
            <div id="strength-bar" style="flex: 1; height: 3px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
              <div id="strength-bar-fill" style="height: 100%; width: 0%; transition: all 0.3s; background: #e0e0e0;"></div>
            </div>
          </div>
          <div id="password-requirements" style="font-size: 10px; color: #666; margin-top: 6px;">
            <div style="margin-bottom: 4px; font-size: 10px; color: #999;">密碼建議：</div>
            <div id="requirement-list" style="line-height: 1.5;"></div>
          </div>
        </div>
      `;
      
      // 添加自定義樣式
      const customStyle = `
        <style>
          .swal2-input {
            padding: 8px 10px !important;
            font-size: 13px !important;
            border-radius: 4px !important;
            border: 1px solid #ddd !important;
            margin: 8px 0 !important;
            height: auto !important;
          }
          .swal2-input:focus {
            border-color: #667eea !important;
            box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1) !important;
          }
          .swal2-popup {
            padding: 20px !important;
            width: 420px !important;
          }
          .swal2-title {
            font-size: 18px !important;
            margin-bottom: 16px !important;
          }
          .swal2-html-container {
            margin: 0 !important;
            padding: 0 !important;
          }
        </style>
      `;
      
      const { value: formValues } = await Swal.fire({
        title: '變更密碼',
        html: `
          ${customStyle}
          <div style="margin-bottom: 4px;">
            <input id="old-password" type="password" class="swal2-input" placeholder="舊密碼" autocomplete="current-password" style="width: 100%;">
          </div>
          <div style="margin-bottom: 4px;">
            <input id="new-password" type="password" class="swal2-input" placeholder="新密碼（至少 8 碼）" autocomplete="new-password" style="width: 100%;">
            ${passwordStrengthHTML}
          </div>
          <div>
            <input id="confirm-password" type="password" class="swal2-input" placeholder="確認新密碼" autocomplete="new-password" style="width: 100%;">
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#667eea',
        cancelButtonColor: '#6c757d',
        didOpen: () => {
          const newPasswordInput = document.getElementById('new-password');
          const strengthText = document.getElementById('strength-text');
          const strengthBarFill = document.getElementById('strength-bar-fill');
          const requirementList = document.getElementById('requirement-list');
          
          // 更新密碼強度指示器
          const updateStrengthIndicator = () => {
            const password = newPasswordInput.value;
            
            if (!password) {
              strengthText.textContent = '-';
              strengthText.style.color = '#666';
              strengthBarFill.style.width = '0%';
              strengthBarFill.style.background = '#e0e0e0';
              requirementList.innerHTML = '';
              return;
            }
            
            const strength = validatePasswordStrength(password);
            
            strengthText.textContent = strength.strengthText;
            strengthText.style.color = strength.strengthColor;
            
            const percentage = (strength.passedCount / strength.totalCount) * 100;
            strengthBarFill.style.width = `${percentage}%`;
            strengthBarFill.style.background = strength.strengthColor;
            
            // 顯示建議列表（第一項為必須，其他為建議）
            const requirements = [
              { key: 'minLength', text: '至少 8 個字元', passed: strength.requirements.minLength, required: true },
              { key: 'hasUpperCase', text: '建議包含大寫字母', passed: strength.requirements.hasUpperCase, required: false },
              { key: 'hasLowerCase', text: '建議包含小寫字母', passed: strength.requirements.hasLowerCase, required: false },
              { key: 'hasNumber', text: '建議包含數字', passed: strength.requirements.hasNumber, required: false },
              { key: 'hasSpecialChar', text: '建議包含特殊字元', passed: strength.requirements.hasSpecialChar, required: false }
            ];
            
            requirementList.innerHTML = requirements.map(req => {
              const color = req.passed ? '#28a745' : (req.required ? '#dc3545' : '#999');
              const icon = req.passed ? '✓' : (req.required ? '✗' : '○');
              const prefix = req.required ? '必須：' : '建議：';
              return `<div style="color: ${color}; font-size: 10px; margin-bottom: 2px;">
                <span style="display: inline-block; width: 12px;">${icon}</span> ${prefix}${req.text}
              </div>`;
            }).join('');
          };
          
          newPasswordInput.addEventListener('input', updateStrengthIndicator);
          updateStrengthIndicator();
        },
        preConfirm: () => {
          const oldPassword = document.getElementById('old-password').value;
          const newPassword = document.getElementById('new-password').value;
          const confirmPassword = document.getElementById('confirm-password').value;

          if (!oldPassword) {
            Swal.showValidationMessage('請輸入舊密碼');
            return false;
          }

          if (!newPassword) {
            Swal.showValidationMessage('請輸入新密碼');
            return false;
          }

          // 驗證密碼長度（只強制 8 碼）
          if (newPassword.length < 8) {
            Swal.showValidationMessage('新密碼至少需要 8 個字元');
            return false;
          }

          if (newPassword !== confirmPassword) {
            Swal.showValidationMessage('新密碼與確認密碼不一致');
            return false;
          }

          return { oldPassword, newPassword, confirmPassword };
        }
      });

      if (formValues) {
        try {
          // 根據帳號類型調用對應的變更密碼 API
          const accountType = authService.getAccountType();
          
          if (!accountType) {
            showError('錯誤', '無法取得帳號類型');
            return;
          }

          // 使用專門的變更密碼 API 端點
          const apiClient = (await import('@/api/axios.js')).default;
          
          let response;
          if (accountType === 'system') {
            response = await apiClient.put('/system/accounts/me/password', {
              oldPassword: formValues.oldPassword,
              newPassword: formValues.newPassword,
              confirmPassword: formValues.confirmPassword
            });
          } else if (accountType === 'platform') {
            response = await apiClient.put('/system/platforms/accounts/me/password', {
              oldPassword: formValues.oldPassword,
              newPassword: formValues.newPassword,
              confirmPassword: formValues.confirmPassword
            });
          } else if (accountType === 'client') {
            response = await apiClient.put('/system/clients/accounts/me/password', {
              oldPassword: formValues.oldPassword,
              newPassword: formValues.newPassword,
              confirmPassword: formValues.confirmPassword
            });
          } else {
            showError('錯誤', '不支援的帳號類型');
            return;
          }

          if (response && response.success) {
            showSuccess('密碼變更成功', '請使用新密碼重新登入');
            // 可選：變更密碼後自動登出
            setTimeout(() => {
              handleLogout(true);
            }, 1500);
          } else {
            showError('變更失敗', response?.message || '密碼變更失敗，請檢查舊密碼是否正確');
          }
        } catch (error) {
          console.error('變更密碼錯誤:', error);
          const errorMessage = error.response?.data?.message || error.message || '密碼變更失敗，請檢查舊密碼是否正確';
          showError('變更失敗', errorMessage);
        }
      }
    };

    // 登出
    const handleLogout = async (skipConfirm = false) => {
      if (!skipConfirm) {
        const confirmed = await showConfirm('確定要登出嗎？', '', '登出', '取消', 'question');
        if (!confirmed) return;
      }
      
      authService.logout();
      menuStore.clearMenu();
      closeUserMenu();
      
      // 根據當前路徑決定導向哪個登入頁
      const currentPath = route.path;
      if (currentPath.startsWith('/system')) {
        router.push('/system/login');
      } else if (currentPath.startsWith('/platform')) {
        router.push('/platform/login');
      } else if (currentPath.startsWith('/client')) {
        router.push('/client/login');
      } else {
        router.push('/system/login');
      }
    };

    // 處理續費按鈕點擊
    const handleRenewSubscription = () => {
      router.push('/client/subscription');
    };

    // 取得訂閱錯誤提示的樣式類別
    const getSubscriptionAlertClass = () => {
      if (!menuStore.subscriptionError) return '';
      const errorType = menuStore.subscriptionError.type;
      if (errorType === 'expired') {
        return 'subscription-alert-danger';
      } else if (errorType === 'grace_period') {
        return 'subscription-alert-warning';
      } else if (errorType === 'expiring_soon') {
        return 'subscription-alert-info';
      }
      return 'subscription-alert-warning';
    };

    // 取得訂閱錯誤提示的圖標
    const getSubscriptionAlertIcon = () => {
      if (!menuStore.subscriptionError) return 'ri-alert-line';
      const errorType = menuStore.subscriptionError.type;
      if (errorType === 'expired') {
        return 'ri-error-warning-line';
      } else if (errorType === 'grace_period') {
        return 'ri-time-warning-line';
      } else if (errorType === 'expiring_soon') {
        return 'ri-time-line';
      }
      return 'ri-alert-line';
    };

    // 載入選單
    onMounted(async () => {
      const isAuth = authService.isAuthenticated();

      if (isAuth) {
        await menuStore.fetchMenu(true); // 強制刷新選單，不使用快取

        // 根據當前路由展開對應模組
        let activeModuleFound = false;
        menuStore.formattedMenu.forEach((module) => {
          module.children.forEach((program) => {
            if (program.path && route.path.startsWith(program.path)) {
              expandedModules.value[module.code] = true;
              activeModuleFound = true;
            }
          });
        });

        // 找不到對應模組時，才預設展開第一個
        if (!activeModuleFound && menuStore.formattedMenu.length > 0) {
          expandedModules.value[menuStore.formattedMenu[0].code] = true;
        }
        
        // 如果是客戶帳號，獲取方案資訊
        if (isClientAccount.value) {
          await fetchClientPlan();
        }
      }

      // 監聽點擊事件，關閉使用者選單
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    // 監聽路由變化，自動展開對應模組
    watch(
      () => route.path,
      (newPath) => {
        // 根據路由路徑自動展開對應模組
        menuStore.formattedMenu.forEach((module) => {
          module.children.forEach((program) => {
            if (newPath.startsWith(program.path)) {
              expandedModules.value[module.code] = true;
            }
          });
        });
      },
      { immediate: true }
    );

    const goToSubscription = () => {
      showUserMenu.value = false;
      router.push('/client/subscription');
    };

    const goToSystemSettings = () => {
      showUserMenu.value = false;
      router.push('/client/settings');
    };

    return {
      route,
      menuStore,
      sidebarCollapsed,
      expandedModules,
      hoveredModules,
      floatingMenuPositions,
      showUserMenu,
      goToSubscription,
      goToSystemSettings,
      currentUser,
      accountType,
      isClientOwnerUser,
      accountTypeText,
      dashboardRoute,
      pageTitle,
      handleRenewSubscription,
      getSubscriptionAlertClass,
      getSubscriptionAlertIcon,
      toggleSidebar,
      toggleModule,
      handleModuleHover,
      handleModuleLeave,
      getFloatingMenuStyle,
      toggleUserMenu,
      closeUserMenu,
      handleChangePassword,
      handleLogout,
      // 浮動視窗相關
      showHelpWindow,
      isHelpWindowMinimized,
      isHelpWindowMaximized,
      helpWindowStyle,
      toggleHelpWindow,
      closeHelpWindow,
      minimizeHelpWindow,
      restoreHelpWindow,
      toggleHelpWindowSize,
      startDrag,
      startResize,
    };
  },
};
</script>

<style scoped>
.main-layout {
  display: flex;
  min-height: 100vh;
  background: #f5f7fa;
}

/* 側邊欄 */
.sidebar {
  width: 260px;
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  overflow: visible; /* 允許浮動選單溢出 */
  /* 固定在視窗左側，不隨頁面捲動 */
  position: sticky;
  top: 0;
  height: 100vh;
  flex-shrink: 0;
  /* 確保 sidebar 的 stacking context 高於主內容區，讓縮小模式的浮動子選單正確顯示在內容上方 */
  z-index: 200;
}

.sidebar.collapsed {
  width: 64px;
  overflow: visible; /* 縮小時也允許溢出 */
}

.topbar.sidebar-collapsed {
  left: 64px;
}

.sidebar.collapsed .sidebar-header {
  padding: 16px 12px;
  justify-content: center;
}

.sidebar.collapsed .logo {
  justify-content: center;
}

.sidebar.collapsed .logo-text {
  display: none;
}

.sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  overflow: hidden;
}

.sidebar.collapsed .sidebar-header {
  padding: 16px 12px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.logo i {
  font-size: 24px;
  color: #667eea;
}

.logo-text {
  white-space: nowrap;
}


/* 選單 */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: visible; /* 允許浮動選單溢出 */
  padding: 12px 0;
  min-height: 0; /* 讓 flex 子元素可以縮小並出現捲軸 */
}

/* 側邊欄縮小時，讓內容可以溢出 */
.sidebar.collapsed .sidebar-nav {
  overflow: visible;
}

.menu-loading,
.menu-error {
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.menu-loading i,
.menu-error i {
  display: block;
  font-size: 24px;
  margin-bottom: 8px;
  color: #999;
}

.menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  margin-bottom: 4px;
}

.menu-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #666;
  text-decoration: none;
  transition: all 0.2s;
  border-left: 3px solid transparent;
}

.menu-link:hover {
  background: #f8f9ff;
  color: #667eea;
}

.menu-link.active {
  background: #f8f9ff;
  color: #667eea;
  border-left-color: #667eea;
  font-weight: 500;
}

.menu-link i {
  font-size: 20px;
  width: 24px;
  text-align: center;
}

.menu-group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.menu-group-header:hover {
  background: #f8f9ff;
  color: #667eea;
}

.menu-group-header i:first-child {
  font-size: 20px;
  width: 24px;
  text-align: center;
}

.menu-arrow {
  margin-left: auto;
  font-size: 16px;
  transition: transform 0.2s;
}

.menu-submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #fafbfc;
}

.menu-subitem {
  margin: 0;
}

.menu-sublink {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px 10px 56px;
  color: #444;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 15px;
}

.menu-sublink:hover {
  background: #f0f2ff;
  color: #667eea;
}

.menu-sublink.active {
  background: #f0f2ff;
  color: #667eea;
  font-weight: 500;
}

.menu-sublink i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

/* 縮小模式的浮動子選單 */
.menu-submenu-floating {
  position: fixed; /* 改為 fixed 定位，避免被切掉 */
  min-width: 200px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  margin: 0;
  z-index: 1001; /* 提高 z-index */
  animation: slideIn 0.2s ease-out;
  max-height: 80vh; /* 限制最大高度 */
  overflow-y: auto; /* 如果內容太多，允許滾動 */
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.menu-submenu-floating .submenu-header {
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 4px;
}

.menu-submenu-floating .submenu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-submenu-floating .menu-subitem {
  margin: 0;
}

.menu-submenu-floating .menu-sublink {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 6px;
  font-size: 15px;
  color: #444;
  text-decoration: none;
  transition: all 0.2s;
}

.menu-submenu-floating .menu-sublink:hover {
  background: #f8f9ff;
  color: #667eea;
}

.menu-submenu-floating .menu-sublink.active {
  background: #f0f2ff;
  color: #667eea;
  font-weight: 500;
}

.menu-submenu-floating .menu-sublink i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

/* 確保 menu-group 有相對定位，讓浮動選單能正確定位 */
.menu-group {
  position: relative;
}

/* 側邊欄底部 */
.sidebar-footer {
  border-top: 1px solid #e0e0e0;
  padding: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 12px;
  color: #999;
}

.user-account-type {
  font-size: 11px;
  color: #667eea;
  background: #f0f3ff;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-block;
  font-weight: 500;
}

.user-plan {
  font-size: 11px;
  color: #666;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-plan i {
  font-size: 13px;
  color: #667eea;
}

.user-plan span {
  font-weight: 500;
}

.logout-btn {
  width: 100%;
  padding: 10px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  color: #c33;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: #fdd;
  border-color: #f99;
}

/* 主內容區 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
}

/* 頂部導航欄 */
.topbar {
  position: fixed;
  top: 0;
  left: 260px;
  right: 0;
  z-index: 100;
  height: 56px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: left 0.3s;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.topbar-plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  font-weight: 500;
}

.topbar-plan-badge i {
  font-size: 14px;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.help-link-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  cursor: pointer;
  border: none; /* 移除邊框 */
  outline: none; /* 移除黑框（focus outline） */
}

.help-link-btn:focus {
  outline: none; /* 移除點擊後的黑框 */
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3); /* 改用柔和的陰影表示 focus 狀態 */
}

.help-link-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.help-link-btn i {
  font-size: 18px;
}

.help-text {
  white-space: nowrap;
}

.user-menu-wrapper {
  position: relative;
}

.user-avatar-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.user-avatar-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 240px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-menu-header {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-menu-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.user-menu-info {
  flex: 1;
  min-width: 0;
}

.user-menu-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-role {
  font-size: 12px;
  color: #999;
}

.user-menu-account-type {
  font-size: 11px;
  color: #667eea;
  background: #f0f3ff;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-block;
  font-weight: 500;
}

.user-menu-plan {
  font-size: 12px;
  color: #666;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.user-menu-plan i {
  font-size: 14px;
  color: #667eea;
}

.user-menu-plan span {
  font-weight: 500;
}

.user-menu-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 8px 0;
}

.user-menu-items {
  padding: 4px 0;
}

.user-menu-item {
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #333;
  transition: all 0.2s;
}

.user-menu-item:hover {
  background: #f8f9ff;
  color: #667eea;
}

.user-menu-item i {
  font-size: 18px;
  width: 20px;
  text-align: center;
}

.user-menu-item-row {
  display: flex;
  align-items: stretch;
}

.user-menu-item--grow {
  flex: 1;
  width: auto;
}

.user-menu-item-newwin {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  flex-shrink: 0;
  color: #9ca3af;
  border-left: 1px solid #f0f0f0;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.user-menu-item-newwin:hover {
  background: #f0f4ff;
  color: #667eea;
}

.user-menu-item-newwin i {
  font-size: 15px;
}

.user-menu-item-newwin::after {
  content: attr(data-tip);
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: #1f2937;
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 5px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}

.user-menu-item-newwin::before {
  content: '';
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-left-color: #1f2937;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}

.user-menu-item-newwin:hover::after,
.user-menu-item-newwin:hover::before {
  opacity: 1;
}

.user-menu-item.logout-item {
  color: #c33;
}

.user-menu-item.logout-item:hover {
  background: #fee;
  color: #c33;
}

/* 頁面內容 */
/* 訂閱過期提示 */
.subscription-alert {
  position: sticky;
  top: 56px;
  z-index: 1001; /* 高於用戶選單 (1000)，確保不被用戶選單覆蓋，但低於 modal 等更高層級元素 */
  width: 100%;
  padding: 12px 20px;
  margin: 0;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.subscription-alert-content {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;
}

.subscription-alert i {
  font-size: 20px;
  flex-shrink: 0;
}

.subscription-alert-message {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
}

.subscription-alert-action {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.subscription-alert-danger {
  background-color: #fee;
  color: #c33;
  border-bottom: 2px solid #c33;
}

.subscription-alert-danger i {
  color: #c33;
}

.subscription-alert-danger .subscription-alert-action {
  background-color: #c33;
  color: white;
}

.subscription-alert-danger .subscription-alert-action:hover {
  background-color: #a22;
}

.subscription-alert-warning {
  background-color: #fff8e1;
  color: #f57c00;
  border-bottom: 2px solid #ffa726;
}

.subscription-alert-warning i {
  color: #f57c00;
}

.subscription-alert-warning .subscription-alert-action {
  background-color: #ffa726;
  color: white;
}

.subscription-alert-warning .subscription-alert-action:hover {
  background-color: #ff9800;
}

.subscription-alert-info {
  background-color: #e3f2fd;
  color: #1976d2;
  border-bottom: 2px solid #42a5f5;
}

.subscription-alert-info i {
  color: #1976d2;
}

.subscription-alert-info .subscription-alert-action {
  background-color: #42a5f5;
  color: white;
}

.subscription-alert-info .subscription-alert-action:hover {
  background-color: #2196f3;
}

.page-content {
  flex: 1;
  padding: 8px;
  margin-top: 56px;
  overflow-y: auto;
  transition: margin-top 0.3s ease-out;
  position: relative;
  z-index: 1; /* 頁面內容的 z-index 設為較低值，因為它不會與訂閱提示重疊（訂閱提示在 topbar 下方） */
}

.page-content.with-subscription-alert {
  margin-top: 56px;
  padding-top: 60px; /* 當有訂閱提示時，增加 padding-top 避免內容被覆蓋 */
}

/* 漢堡選單按鈕（在 topbar 中，統一使用） */
.mobile-menu-toggle {
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
  padding: 8px;
  margin-right: 12px;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
}

.mobile-menu-toggle:hover {
  background: #f0f0f0;
}

/* 遮罩層（預設隱藏，只在移動端顯示） */
.sidebar-overlay {
  display: none;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s;
    width: 260px;
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  /* 側邊欄顯示時的遮罩層 */
  .sidebar-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .main-content {
    margin-left: 0;
    width: 100%;
  }

  /* 移動端 topbar 固定在頂部 */
  .topbar {
    left: 0 !important;
  }

  /* 調整 topbar 標題 */
  .topbar-left {
    display: flex;
    align-items: center;
  }

  /* 手機版簡化操作說明按鈕 */
  .help-link-btn {
    padding: 8px;
    min-width: 40px;
    justify-content: center;
  }

  .help-text {
    display: none;
  }
}

/* 浮動式操作說明視窗 */
.help-window {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 10000;
  min-width: 400px;
  min-height: 300px;
  overflow: hidden;
}

.help-window-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: move;
  user-select: none;
}

.help-window-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
}

.help-window-title i {
  font-size: 18px;
}

.help-window-actions {
  display: flex;
  gap: 4px;
}

.help-window-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.help-window-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.help-window-btn.help-window-close:hover {
  background: #e74c3c;
}

.help-window-btn i {
  font-size: 16px;
}

.help-window-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #f8f9fa;
}

.help-window-content iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.help-window-resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 50%, rgba(102, 126, 234, 0.3) 50%);
  border-bottom-right-radius: 12px;
}

.help-window-resize-handle:hover {
  background: linear-gradient(135deg, transparent 50%, rgba(102, 126, 234, 0.5) 50%);
}

/* 最小化狀態的浮動按鈕 */
.help-window-minimized {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s;
  z-index: 9999;
  animation: helpButtonPulse 2s infinite;
}

.help-window-minimized:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.help-window-minimized i {
  font-size: 18px;
}

@keyframes helpButtonPulse {
  0%, 100% {
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  }
  50% {
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.6), 0 0 0 8px rgba(102, 126, 234, 0.1);
  }
}

/* 手機版浮動視窗調整 */
@media (max-width: 768px) {
  .help-window {
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0;
  }

  .help-window-minimized {
    bottom: 80px;
    right: 16px;
    padding: 10px 20px;
    font-size: 13px;
  }

  .help-window-minimized span {
    display: inline;
  }
}
</style>
